// Apply scenario + period range to the raw dataset.
// Scenarios (two-state toggle):
//   - "confirmed"            → only contract === "Yes", full value ("Confirmed")
//   - "confirmed_tentative"  → contract in ["Yes","Tentative"], full value ("Forecast" —
//                              the "if we win everything currently proposed" ceiling)
// Period range:
//   - array of "YYYY-MM" month keys — used as the new meta.months, so calc
//     helpers that iterate months(data) automatically narrow to this window.
//   - months in the range that aren't present in the stored dataset just show
//     as $0 cells (rolling display can show empty future months).

import { fmtMonthShort } from "./format";
import {
  currentMonthKey,
  monthRange,
  addMonths,
  unionMonths,
  diffMonths,
} from "./dateWindow";

export const SCENARIOS = {
  confirmed: { id: "confirmed", label: "Confirmed" },
  confirmed_tentative: { id: "confirmed_tentative", label: "Forecast" },
};

export const DEFAULT_SCENARIO = "confirmed_tentative";

export function applyScenario(rawData, scenario, monthsRange) {
  if (!rawData) return rawData;

  const baseMonths = rawData.meta?.months ?? [];
  const months = (monthsRange && monthsRange.length) ? monthsRange : baseMonths;

  // Classification is PER SCOPE ROW — a mixed project (some Confirmed, some
  // Tentative scopes) stays visible under Confirmed but contributes only
  // its Confirmed rows. Sub-consultant rows follow their OWN contract status
  // like any other row: keeping them unconditionally used to put them inside
  // the scenario totals but outside the Backlog/Pipeline split, which broke
  // `Confirmed = Backlog` the moment a sub fee was spread across months.
  const keep = scenario === "confirmed"
    ? (s) => s.contract === "Yes"
    : (s) => s.contract === "Yes" || s.contract === "Tentative";

  // A project with no fee row of its own is normally outside scenario scope.
  // The exception is a RESOURCING SHELL: principals receive other principals'
  // projects with `scopes` stripped server-side (revenue must stay hidden) but
  // with their allocations intact, and dropping those here erased every other
  // principal's load from the capacity matrix. Keep such a project when it
  // carries allocations — with scopes emptied so it can never reach a revenue
  // figure — and classify it by its project-level contract flag, which is all
  // a shell has, so the scenario selector still applies to it.
  const shellInScenario = (p) =>
    p.contract === "Yes" || (scenario !== "confirmed" && p.contract === "Tentative");

  const adjusted = (rawData.projects ?? [])
    .map((p) => ({
      ...p,
      scopes: (p.scopes || [])
        .filter(keep)
        .map((s) => ({ ...s, probability: 1, _originalProbability: s.probability ?? 1 })),
    }))
    .map((p) => {
      if ((p.scopes || []).some((s) => !s.isSubConsultant)) return p;
      return (p.allocations || []).length && shellInScenario(p) ? { ...p, scopes: [] } : null;
    })
    .filter(Boolean);

  return {
    ...rawData,
    meta: { ...rawData.meta, months },
    projects: adjusted,
  };
}

// Default rolling window: current real month + 11 forward = 12 months.
// Unioned with any stored months from rawData so past months with data
// (referenced via custom range) remain reachable — but the DEFAULT visible
// window is strictly the forward-looking 12.
export const defaultPeriodMonths = () => monthRange(currentMonthKey(), 12);

// Period presets — 1 month / 1 year / 2 years, anchored on the current
// real month (rolling behaviour). Every window slides forward automatically.
// `storedMonths` (rawData.meta.months) is unioned in for the labels so the
// custom picker sees the true extent of the data.
export function buildPeriodPresets(storedMonths) {
  const cur = currentMonthKey();
  const stored = storedMonths || [];
  const rangeLabel = (ms) =>
    ms.length === 1 ? fmtMonthShort(ms[0])
                    : `${fmtMonthShort(ms[0])} – ${fmtMonthShort(ms[ms.length - 1])}`;

  const oneMonth = [cur];
  const oneYear = monthRange(cur, 12);
  const twoYears = monthRange(cur, 24);
  // Backward-looking 12 months — for reviewing past performance without
  // opening the custom picker.
  const lastYear = monthRange(addMonths(cur, -11), 12);

  return [
    { id: "1m", label: `1 month · ${rangeLabel(oneMonth)}`, months: oneMonth },
    { id: "1y", label: `1 year · ${rangeLabel(oneYear)}`, months: oneYear },
    { id: "2y", label: `2 years · ${rangeLabel(twoYears)}`, months: twoYears },
    { id: "last12", label: `Last 12 months · ${rangeLabel(lastYear)}`, months: lastYear },
  ];
}

// The complete set of months a custom picker can offer: current window
// unioned with all stored months (so past months with data are selectable).
export function pickableMonths(storedMonths) {
  const stored = storedMonths || [];
  const cur = currentMonthKey();
  // Allow reaching 24 months backward from today too (past custom range).
  const past = monthRange(addMonths(cur, -24), 24);
  const future = monthRange(cur, 24);
  return unionMonths(unionMonths(past, future), stored);
}

// Small helper used by DataContext to know if the currently-selected
// months are the "rolling default".
export const isRollingDefault = (months) => {
  const def = defaultPeriodMonths();
  if (!months || months.length !== def.length) return false;
  for (let i = 0; i < def.length; i++) if (months[i] !== def[i]) return false;
  return true;
};

// Debug/testing hook — return the label the toolbar uses.
export const periodShowingLabel = (months) => {
  if (!months?.length) return "0 of 0 months";
  return `${months.length} of ${months.length} months`;
};

// Diff helper re-export for consumers that only import from scenario.
export { diffMonths };
